import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { AlertController } from '@ionic/angular';
import { addDays, isSameDay } from 'date-fns';
import { EnodeService } from 'src/app/services/enode/enode.service';
import {
  EnodeVehicle,
  SmartChargingPlan,
  SmartChargingStatus
} from 'src/app/services/enode/enode.types';
import { hasChanged } from 'src/app/utils/change-utils';

enum SmartChargePlanEndedState {
  FINISHED = 'PLAN:ENDED:FINISHED',
  FAILED = 'PLAN:ENDED:FAILED',
  DISABLED = 'PLAN:ENDED:DISABLED',
  DEADLINE_CHANGED = 'PLAN:ENDED:DEADLINE_CHANGED',
  UNPLUGGED = 'PLAN:ENDED:UNPLUGGED'
}

@Component({
  selector: 'app-smart-charging',
  templateUrl: './smart-charging.component.html',
  styleUrls: ['./smart-charging.component.scss']
})
export class SmartChargingComponent implements OnInit, OnChanges {
  @Input() vehicle: EnodeVehicle;
  @Input() smartChargeStatus: SmartChargingStatus;

  policyEnabled: boolean;
  deadline: string;
  minimumChargeLimit: number;

  debounceTimer: NodeJS.Timeout;
  policyErrorMessage: string;
  policySuccessMessage: string;
  hasSmartChargePlan = false;
  smartChargeStart: string;
  smartChargeEnd: string;
  smartChargeEndsTomorrow: boolean;
  smartChargePaused: string;
  estimatedSmartChargeTime: string;
  estimatedSmartChargeSavings: string;
  estimatedSmartChargeCost: string;
  accordionExpanded = false;
  startTime: Date;
  endTime: Date;
  meetsConsideration: boolean;
  isWaitingForPlan: boolean;
  isOverridden = false;

  constructor(
    private _enodeService: EnodeService,
    private _alertCtrl: AlertController
  ) {}

  ngOnInit() {
    this.policyErrorMessage = '';
    this.policySuccessMessage = '';
    this.policyEnabled = this.vehicle.capabilities.smartCharging.isCapable
      ? this.vehicle.smartChargingPolicy.isEnabled
      : false;
    this.deadline = this.vehicle.smartChargingPolicy.deadline ?? '07:00';
    this.minimumChargeLimit =
      this.vehicle.smartChargingPolicy.minimumChargeLimit ?? 0;
    this.meetsConsideration =
      this.smartChargeStatus?.consideration?.atChargingLocation !== false &&
      this.smartChargeStatus?.consideration?.hasTimeEstimate !== false &&
      this.smartChargeStatus?.consideration?.isCharging !== false &&
      this.smartChargeStatus?.consideration?.isPluggedIn !== false;

    if (
      this.smartChargeStatus?.plan?.id &&
      this.smartChargeStatus?.plan?.startAt &&
      this.smartChargeStatus?.plan?.estimatedFinishAt
    ) {
      this.hasSmartChargePlan = true;
      this.parseSmartChargePlan(this.smartChargeStatus.plan);
    }
    if (this.smartChargeStatus?.state === 'PLAN:EXECUTING:OVERRIDDEN') {
      this.isOverridden = true;
    }
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (
      !simpleChanges.vehicle?.firstChange &&
      !simpleChanges.smartChargeStatus?.firstChange &&
      hasChanged([simpleChanges.vehicle, simpleChanges.smartChargeStatus])
    ) {
      if (simpleChanges?.vehicle?.currentValue) {
        const policy = simpleChanges.vehicle.currentValue.smartChargingPolicy;
        this.policyEnabled = policy.isEnabled;
        this.deadline = policy.deadline ?? '07:00';
        this.minimumChargeLimit = policy.minimumChargeLimit ?? 0;
      }
      if (simpleChanges?.smartChargeStatus?.currentValue) {
        const state = simpleChanges.smartChargeStatus.currentValue.state;
        if (state in SmartChargePlanEndedState) {
          this.hasSmartChargePlan = false;
        } else if (simpleChanges?.smartChargeStatus.currentValue.plan?.id) {
          this.hasSmartChargePlan = true;
          this.isWaitingForPlan = false;
          this.parseSmartChargePlan(
            simpleChanges.smartChargeStatus.currentValue.plan
          );
        }
        this.meetsConsideration =
          simpleChanges.smartChargeStatus.currentValue.consideration
            ?.atChargingLocation !== false &&
          simpleChanges.smartChargeStatus.currentValue.consideration
            ?.hasTimeEstimate !== false &&
          simpleChanges.smartChargeStatus.currentValue.consideration
            ?.isCharging !== false &&
          simpleChanges.smartChargeStatus.currentValue.consideration
            ?.isPluggedIn !== false;
        this.isWaitingForPlan = false;

        if (
          simpleChanges?.smartChargeStatus?.currentValue?.state ===
          'PLAN:EXECUTING:OVERRIDDEN'
        ) {
          this.isOverridden = true;
        } else {
          this.isOverridden = false;
        }
      }
    }
  }

  updatePolicy() {
    this._enodeService
      .updateSmartChargePolicy(this.vehicle.id, {
        isEnabled: this.policyEnabled,
        deadline: this.deadline,
        minimumChargeLimit: this.minimumChargeLimit
      })
      .subscribe({
        next: (res) => {
          this.policyEnabled = res.isEnabled;
          this.deadline = res.deadline;
          this.minimumChargeLimit = res.minimumChargeLimit;
          this.policySuccessMessage = 'Oppdatert!';
          this.hasSmartChargePlan = false;
          if (res.isEnabled) {
            this.isWaitingForPlan = true;
          }
        },
        error: (err) => {
          this.policyErrorMessage = 'Noe gikk galt...';
          this.policyEnabled = this.vehicle.smartChargingPolicy.isEnabled;
          this.deadline = this.vehicle.smartChargingPolicy.deadline ?? '07:00';
        }
      });
  }

  toggleSmartCharging() {
    this.policyErrorMessage = '';
    this.policySuccessMessage = '';
    this.updatePolicy();
  }

  setDeadline(time: string) {
    this.deadline = time;
    this.debounce(this.updatePolicy)();
  }

  debounce(func: Function) {
    return (...args) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        func.apply(this, args);
      }, 1000);
    };
  }

  parseSmartChargePlan(plan: SmartChargingPlan) {
    this.startTime = new Date(plan.startAt);
    this.endTime = new Date(plan.estimatedFinishAt);
    this.smartChargeStart = new Date(plan.startAt).toLocaleTimeString('nb-NO', {
      hour: '2-digit',
      minute: '2-digit'
    });
    this.smartChargeEnd = new Date(plan.estimatedFinishAt).toLocaleTimeString(
      'nb-NO',
      {
        hour: '2-digit',
        minute: '2-digit'
      }
    );
    this.smartChargeEndsTomorrow = isSameDay(
      new Date(plan.estimatedFinishAt),
      addDays(new Date(), 1)
    );

    this.smartChargePaused = new Date(plan.stopAt).toLocaleTimeString('nb-NO', {
      hour: '2-digit',
      minute: '2-digit'
    });

    const estimatedTime = Math.abs(
      new Date(plan.estimatedFinishAt).getTime() -
        new Date(plan.startAt).getTime()
    );
    const estHour = Math.floor(estimatedTime / 1000 / 60 / 60);
    const estMin = Math.floor((estimatedTime / 1000 / 60 / 60 - estHour) * 60);
    this.estimatedSmartChargeTime = `${estHour}t ${estMin}m`;

    if (plan?.smartCost && plan?.nonSmartCost) {
      this.estimatedSmartChargeSavings = (
        plan.nonSmartCost - plan.smartCost
      ).toFixed(2);
      this.estimatedSmartChargeCost = plan.smartCost.toFixed(2);
    }
  }

  accordionGroupChange(event: any) {
    if (event?.detail?.value) {
      this.accordionExpanded = true;
    } else {
      this.accordionExpanded = false;
    }
  }

  async openHelpCost() {
    const alert = await this._alertCtrl.create({
      mode: 'ios',
      header: 'Hvordan beregnes besparelsen?',
      message:
        'På dager med store prisvariasjoner kan du spare penger ved å flytte lading til de billigste timene. Hvor mye du sparer, regnes ut som differansen mellom hva det ville kostet å lade bilen fra det tidspunktet den ble koblet til laderen, og hva det endte opp med å koste å lade bilen etter at smartladingsalgoritmen utsatte lading til de billigste timene på døgnet. Vi tar forbehold om feil.',
      buttons: ['Ok']
    });
    await alert.present();
  }
}
